<template>
	<Panel
		title="Deal Checklist"
		class="panel-deal-checklist ignore-deal-lock"
	>
		<template #HeaderComponent>
			<ButtonLoading
				:isLoading="isLoading || isIniting"
				class="button ignore-all-locks"
				:disabled="isPolling"
				@click="init"
			>
				<i :class="{ 'fas fa-sync': true, 'fa-spin': isPolling }" /> Refresh
			</ButtonLoading>

			<!-- TODO: Should link to the checklist page on customer portal -->
			<!-- <DropdownButton label="Send Checklist Link"
                :actions="sendLinkActions"
                :disabled="fimenu.isSpectator || isLoading"
            /> -->
		</template>

		<IsBusySectionComponent
			v-if="isIniting"
			style="height: unset"
		/>

		<DealChecklistDisplay
			v-else
			:fimenu="fimenu"
			:checklist="filteredChecklist"
			:disableAllActions="isLoading"
			showDetailedTable
			@refreshItem="refreshChecklistItem"
			@refreshAllItems="refreshAllChecklistItems"
		/>
	</Panel>
</template>

<script lang="ts">
	export interface IPanelDealChecklistProps {
		/** Used when fetching external data for the checklist */
		fimenu: FIMenu;

		/** Prevent the panel from automatically polling for new data */
		disablePolling?: boolean;

		/** Will hide items that only have section dependencies on sections after the given section. */
		excludeSectionsAfter?: FIMenuSection;
	}

	export interface IDealParticipantTaskInfo {
		name: string;
		role: PersonType;
		roleDisplay: string;
		tasksCompleted: number;
		totalTasks: number;
	}
</script>
<script setup lang="ts">
	import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs, watch } from 'vue';

	import api from '@core/services/api/_api';
	import auth from '@core/services/auth';
	import ChecklistHelper from '@/helpers/checklist-helper';
	import util from '@core/services/util';

	import DealChecklistItem, { ChecklistVerifyType } from '@core/classes/Checklist/DealChecklistItem';
	import { FIMenuSection, PersonType } from '@core/classes/SharedEnums';
	import { BitFlagHelper } from '@core/helpers/enum-helper';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuSectionEnumsHelper from '@core/_enums/FIMenuSectionEnumsHelper';

	import ButtonLoading from '@core/components/ButtonLoading.vue';
	import DealChecklistDisplay from '@/components/DealChecklistDisplay.vue';
	//import DropdownButton from "@/components/DropdownButton.vue";
	import IsBusySectionComponent from '@/components/IsBusySectionComponent.vue';
	import Panel from '@core/components/Panel.vue';
	import { SaturnPermissions } from '@core/classes/AccessEmployeeClass';

	const props = defineProps<IPanelDealChecklistProps>();
	const { fimenu } = toRefs(props);
	const user = auth.getTokenPayload();

	const emit = defineEmits<{
		loading: [currValue: boolean];
		checklistUpdate: [currValue: DealChecklistItem[]];
	}>();

	// =========
	// CONSTANTS
	// =========
	// #region Constants
	const USER_PERMISSIONS = user?.getChecklistPermissionFlags() ?? SaturnPermissions.None;
	// #endregion

	// =============
	// GENERAL STATE
	// =============
	// #region General State
	const isIniting = ref(false);
	const isLoading = ref(false);

	watch(isLoading, newValue => {
		emit('loading', newValue);
	});

	const checklist = ref<DealChecklistItem[]>([]);

	const filteredChecklist = computed((): DealChecklistItem[] => {
		if (!props.excludeSectionsAfter || props.excludeSectionsAfter === FIMenuSection.Unknown) return checklist.value;

		const sectionOrder = FIMenuSectionEnumsHelper.getOrder(props.excludeSectionsAfter);

		// Can't filter if this section is unordered
		if (sectionOrder == null) return checklist.value;

		// Hide items where all of its section dependencies are ordered after the section filter.
		const newChecklist = checklist.value.filter(item => {
			const allSectionsAreAfter = item.allSectionDependencies.every(d => {
				const dOrder = FIMenuSectionEnumsHelper.getOrder(d.section);

				// Don't count unordered items
				if (dOrder == null) return false;

				return sectionOrder < dOrder;
			});

			return !allSectionsAreAfter;
		});

		/*
		 * Since we requested for all available section dependencies, manual / optional dependencies are
		 * combined between ALL section dependencies. We'll have to recompute this as if the future sections
		 * don't exist.
		 */
		newChecklist.forEach(item => {
			let newRequiredVerifyBy = SaturnPermissions.None;
			let newOptionalVerifyBy = SaturnPermissions.None;

			for (const dependency of item.allSectionDependencies) {
				// Skipping over future sections
				const currOrder = FIMenuSectionEnumsHelper.getOrder(dependency.section);
				if (currOrder != null && sectionOrder < currOrder) continue;

				newRequiredVerifyBy = BitFlagHelper.addFlag(newRequiredVerifyBy, dependency.requiredVerificationBy);
				newOptionalVerifyBy = BitFlagHelper.addFlag(newOptionalVerifyBy, dependency.optionalVerificationBy);
			}

			item.needsVerifyBy = newRequiredVerifyBy;
			item.optionalVerifyBy = newOptionalVerifyBy;
		});

		return newChecklist;
	});

	watch(checklist, newValue => {
		emit('checklistUpdate', newValue);
	});
	// #endregion

	// =================
	// DEAL PARTICIPANTS
	// =================
	// #region Deal Participants
	const dealParticipants = computed((): IDealParticipantTaskInfo[] => {
		const tasksCompleted = checklist.value.filter(c => {
			return c.requiresManualVerification(ChecklistVerifyType.Required) && c.hasAnyManualVerifications()
				? c.isTaskCompleteForUser(ChecklistVerifyType.Required, USER_PERMISSIONS)
				: c.isVerified();
		}).length;

		const dealParticipants: IDealParticipantTaskInfo[] = [
			{
				name: 'All Tasks',
				role: PersonType.Unknown,
				roleDisplay: 'All Tasks',
				totalTasks: checklist.value.length,
				tasksCompleted: tasksCompleted,
			},
		];

		const customerTasks = checklist.value.filter(c => c.canSendTo(PersonType.Customer));
		if (customerTasks.length > 0) {
			const customerTasksCompleted = customerTasks.filter(c => c.isTaskCompleteForCustomer()).length;

			dealParticipants.push({
				name: fimenu.value.customer.fullName?.toLowerCase() ?? 'Customer',
				role: PersonType.Customer,
				roleDisplay: 'Customer',
				totalTasks: customerTasks.length,
				tasksCompleted: customerTasksCompleted,
			});
		}

		const coCustomerTasks = checklist.value.filter(c => c.canSendTo(PersonType.CoCustomer));
		if (coCustomerTasks.length > 0 && fimenu.value.coCustomer && fimenu.value.coCustomer.cell) {
			const coCustomerTasksCompleted = coCustomerTasks.filter(c => c.isTaskCompleteForCustomer()).length;

			dealParticipants.push({
				name: fimenu.value.coCustomer.fullName?.toLowerCase() ?? 'Co-Customer',
				role: PersonType.CoCustomer,
				roleDisplay: 'Co-Customer',
				totalTasks: coCustomerTasks.length,
				tasksCompleted: coCustomerTasksCompleted,
			});
		}

		// Only returns tasks that FI Manager can actively act on.
		const fiManagerTasks = checklist.value.filter(c => {
			return c.isPendingVerification(ChecklistVerifyType.Required, USER_PERMISSIONS) && !c.status.isManuallyRejected;
		});

		dealParticipants.push({
			name: 'Deal Employee',
			role: PersonType.FIManager,
			roleDisplay: 'Your Tasks',
			totalTasks: fiManagerTasks.length,
			tasksCompleted: fiManagerTasks.filter(c => c.isTaskCompleteForUser(ChecklistVerifyType.Required, USER_PERMISSIONS)).length,
		});

		return dealParticipants;
	});

	const sendLinkActions = computed((): { label: string; role: number; click: () => void }[] => {
		const filteredParticipants = dealParticipants.value.filter(p => p.role !== PersonType.FIManager && p.role !== PersonType.Unknown);
		if (!filteredParticipants || filteredParticipants.length < 1) return [];

		return filteredParticipants.map(p => {
			return {
				label: p.name,
				role: p.role,
				click: () => sendChecklistLink(p.role),
			};
		});
	});
	// #endregion

	// =======
	// POLLING
	// =======
	// #region Polling
	const isPolling = ref(false);
	const pollingTimeout = ref<NodeJS.Timeout>(null);
	const pollingCounter = ref(0);

	const canStopPolling = computed(() => {
		if (!dealParticipants.value || dealParticipants.value.length < 1) return true;

		const allTasksList = dealParticipants.value.find(p => p.role === PersonType.Unknown);
		return allTasksList.tasksCompleted === allTasksList.tasksCompleted;
	});

	watch(canStopPolling, newValue => {
		startChecklistPolling();
	});

	const startChecklistPolling = () => {
		clearTimeout(pollingTimeout.value);
		if (canStopPolling.value || isUnmounting.value || props.disablePolling) return;

		// As the counter goes up, increase polling delay exponentially
		const pollingDelay = 100 * Math.pow(2, pollingCounter.value);
		pollingTimeout.value = setTimeout(pollForChecklistUpdates, pollingDelay);
	};

	const pollForChecklistUpdates = async () => {
		isPolling.value = true;

		try {
			const response = await api.checklist.fimenu_getAllRequired(fimenu.value.id);
			if (!response?.data) throw new Error('No response from server.', { cause: response });

			const updatedChecklist: DealChecklistItem[] = response.data.map((c: Partial<DealChecklistItem>) => new DealChecklistItem(c));
			const anyChanges = checklist.value.some(oldC => {
				const newItem = updatedChecklist.find(newC => newC.id === oldC.id && newC.setIndex === oldC.setIndex);

				// A new item was added
				if (!newItem) return true;
				return oldC.anyStatusChanges(newItem);
			});

			if (anyChanges) {
				pollingCounter.value = 0;
				checklist.value = updatedChecklist;
			} else {
				pollingCounter.value++;
			}

			if (!props.disablePolling) startChecklistPolling();
		} catch (error) {
			// No response from server. Stop polling.
			console.error('ERROR POLLING FOR CHECKLIST UPDATES: ', error);
			util.toastr('error', 'Error While Polling', 'An error occurred while polling for updates.');
		} finally {
			isPolling.value = false;
		}
	};
	// #endregion

	// ===============
	// LIFECYCLE HOOKS
	// ===============
	// #region Lifecycle Hooks
	const isUnmounting = ref(false);

	onBeforeMount(async () => {
		isIniting.value = true;
		try {
			await init();
		} finally {
			isIniting.value = false;
		}
	});

	onBeforeUnmount(() => {
		isUnmounting.value = true;
		clearTimeout(pollingTimeout.value);
	});
	// #endregion

	// ===============
	// INITIALIZE DATA
	// ===============
	// #region Initialize Data
	const init = async () => {
		clearTimeout(pollingTimeout.value);

		isPolling.value = false;
		isLoading.value = true;

		try {
			await getFIMenuChecklist();
			pollingCounter.value = 0;

			if (!props.disablePolling) startChecklistPolling();
		} catch (err) {
			console.error('ERROR INITING DEAL CHECKLIST PANEL', err);
			util.toastr('error', 'Error Fetching Checklist Data', 'An error occurred when trying to fetch the checklist data.');
		} finally {
			isLoading.value = false;
		}
	};

	const getFIMenuChecklist = async () => {
		try {
			const response = await api.checklist.fimenu_getAllRequired(fimenu.value.id);
			if (!response?.data) throw new Error('No response from server.', { cause: response });

			checklist.value = response.data.map((i: Partial<DealChecklistItem>) => new DealChecklistItem(i));
		} catch (error) {
			console.error('ERROR FETCHING FIMENU CHECKLIST:', error);
			util.toastr('error', 'Retrieving FiMenu Checklist', (error as Error)?.message ?? 'An unknown server error occurred.');
		}
	};

	const refreshAllChecklistItems = async () => {
		isLoading.value = true;

		try {
			await getFIMenuChecklist();
		} finally {
			isLoading.value = false;
		}
	};

	const refreshChecklistItem = async (checklistItem: DealChecklistItem) => {
		isLoading.value = true;

		try {
			const response = await api.checklist.fimenu_getItem(fimenu.value.id, checklistItem.id, checklistItem.setIndex);
			if (!response?.data) throw new Error('No response from server.', { cause: response });

			const updatedItem = new DealChecklistItem(response.data);
			const currItemIndex = checklist.value.findIndex(c => c.id === updatedItem.id && c.setIndex === checklistItem.setIndex);

			checklist.value.splice(currItemIndex, 1, updatedItem);
		} catch (err) {
			console.error(`ERROR REFRESHING ITEM "${checklistItem.name}": `, err);
			util.toastr('error', `Error Refreshing Item "${checklistItem.name}"`, (err as Error)?.message ?? 'An unknown server error occurred.');
		} finally {
			isLoading.value = false;
		}
	};
	// #endregion

	const sendChecklistLink = async (forWhom: PersonType) => {
		ChecklistHelper.sendLinkToCustomer(fimenu.value, forWhom, (busyValue: boolean) => {
			isLoading.value = busyValue;
		});
	};
</script>
<style>
	.panel-deal-checklist .panel-header .header-component {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.panel-deal-checklist .panel-header .header-component > button {
		flex: 1;
		gap: 5px;
	}

	.panel-deal-checklist .panel-header .header-component .button-dropdown-container button {
		text-transform: capitalize;
	}

	.panel-deal-checklist .checklist-summary .active-person {
		position: relative;
		border-radius: 10px 10px 0 0;
		border-bottom: 2px solid var(--main-color);
		background-color: var(--card-background-color);
		box-shadow: -2px -3px 4px 1px rgba(0, 0, 0, 0.25);
		transition: all 0.3s ease-in-out;
		z-index: -1;
	}

	.panel-deal-checklist .checklist-summary .progress-circle-container:hover,
	.panel-deal-checklist .checklist-summary .active-person .progress-circle-container {
		color: var(--main-color);
	}

	.panel-deal-checklist .checklist-summary .progress-circle-container.all-tasks-circle label small {
		visibility: hidden;
	}
</style>
